/** Backup of the previous public marketing landing. Served at /landing-classic. */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import ChatWidget from "@/components/ChatWidget";
import SiteHead from "@/components/SiteHead";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { POLICIES } from "@/lib/policies";
import { mediaUrl, platformDisplayHost } from "@/lib/api";
import { normalizeStorefrontTheme } from "@/lib/storefrontTheme";
import { CreateStoreWithTheme } from "@/components/CarPartsOnboardDialog";
import {
  Rocket, ShieldCheck, Search, Heart, Ticket, Percent, Package, Sparkles,
  ShoppingCart, UserPlus, RefreshCw, IndianRupee, ArrowRight, CheckCircle2,
  Smartphone, Zap, Star, MoveRight, BadgeCheck, Store, Play, Quote,
  Menu, X, ChevronLeft, ChevronRight, Truck, ClipboardList,
} from "lucide-react";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null; // treat as a direct video file
}

function VideoPlayer({ url, poster, vertical = false, rounded = "rounded-2xl" }) {
  const [playing, setPlaying] = useState(false);
  const embed = toEmbed(url);
  const aspect = vertical ? "aspect-[9/16]" : "aspect-video";
  return (
    <div className={`relative ${aspect} w-full bg-stone-900 border-2 border-stone-900 lp-hard overflow-hidden ${rounded}`}>
      {!playing ? (
        <button
          type="button"
          onClick={() => url && setPlaying(true)}
          className="group absolute inset-0 w-full h-full"
          data-testid="video-play"
          aria-label="Play video"
        >
          {poster ? (
            <img src={mediaUrl(poster)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-stone-800 to-stone-900" />
          )}
          <span className="absolute inset-0 bg-stone-900/25 group-hover:bg-stone-900/10 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-white text-stone-900 border-2 border-stone-900 lp-hard-sm flex items-center justify-center group-hover:scale-105 transition-transform">
              <Play className="w-7 h-7 ml-1 fill-stone-900" />
            </span>
          </span>
        </button>
      ) : embed ? (
        <iframe src={embed} title="video" className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      ) : (
        <video src={url} poster={mediaUrl(poster)} controls autoPlay playsInline className="w-full h-full object-cover" />
      )}
    </div>
  );
}

function Stars({ n = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "text-amber-400 fill-amber-400" : "text-stone-300"}`} />
      ))}
    </div>
  );
}

const FEATURES = [
  { icon: Rocket, title: "Instant ecommerce creation", body: "Pick a URL, add products, go live the same afternoon.", span: "md:col-span-2", tone: "orange" },
  { icon: ShoppingCart, title: "Cart, checkout & SSL", body: "A smooth, secure checkout — HTTPS on every store, free.", tone: "ink" },
  { icon: RefreshCw, title: "Abandoned cart recovery", body: "Win back shoppers who almost bought.", tone: "ink" },
  { icon: IndianRupee, title: "Razorpay · Cashfree · WhatsApp · COD", body: "Take money the way Indian shoppers actually pay.", span: "md:col-span-2", tone: "emerald" },
  { icon: Search, title: "SEO built-in", body: "Sitemaps, meta tags & rich results — found on Google.", tone: "ink" },
  { icon: UserPlus, title: "Login / Signup", body: "Customer accounts & order history out of the box.", tone: "ink" },
  { icon: Percent, title: "Discounts", body: "Flat or percentage — run offers in seconds.", tone: "ink" },
  { icon: Heart, title: "Wishlist", body: "Let shoppers save what they love.", tone: "ink" },
  { icon: Ticket, title: "Coupons", body: "Codes, limits & campaigns made simple.", tone: "ink" },
  { icon: Package, title: "Up to 100 products", body: "Plenty of room to grow your catalog.", tone: "ink" },
  { icon: Truck, title: "Shipping & delivery", body: "Set shipping rates, zones & free-delivery thresholds.", tone: "ink" },
  { icon: ClipboardList, title: "Order management", body: "Track, update & fulfil every order from one dashboard.", tone: "ink" },
  { icon: Sparkles, title: "+80 more features", body: "Variants, pages, media, analytics & lots more.", span: "md:col-span-2", tone: "orange" },
];

const toneMap = {
  orange: "bg-orange-100 text-orange-700",
  emerald: "bg-emerald-100 text-emerald-700",
  ink: "bg-stone-900 text-white",
};

function BrowserMock() {
  return (
    <div className="rounded-2xl bg-white border-2 border-stone-900 lp-hard overflow-hidden">
      <div className="flex items-center gap-2 px-3 h-9 bg-stone-100 border-b-2 border-stone-900">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <div className="ml-2 flex-1 h-5 rounded-full bg-white border border-stone-300 flex items-center px-3 text-[10px] text-stone-500 font-mono-nums truncate">
          {platformDisplayHost() || "yourstore.example"}/fresh-fruits
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-md bg-emerald-600 flex items-center justify-center"><Store className="w-3 h-3 text-white" /></span>
            <span className="text-[11px] font-bold text-stone-900">Fresh Fruits & Grocery</span>
          </div>
          <ShoppingCart className="w-4 h-4 text-stone-500" />
        </div>
        <div className="h-16 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 mb-3 flex items-center px-4">
          <div>
            <div className="text-white text-xs font-bold">Fresh, daily.</div>
            <div className="text-white/80 text-[9px]">Free delivery over ₹499</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[["#FDE68A", "Mango"], ["#BBF7D0", "Spinach"], ["#FBCFE8", "Apple"], ["#FED7AA", "Papaya"], ["#C7D2FE", "Grapes"], ["#FECACA", "Tomato"]].map(([c, n], i) => (
            <div key={i} className="rounded-lg border border-stone-200 overflow-hidden">
              <div className="h-10" style={{ background: c }} />
              <div className="px-1.5 py-1">
                <div className="text-[8px] font-semibold text-stone-700 truncate">{n}</div>
                <div className="text-[8px] text-emerald-700 font-mono-nums">₹{40 + i * 15}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhoneMock() {
  return (
    <div className="w-[150px] rounded-[1.8rem] bg-stone-900 p-1.5 border-2 border-stone-900 lp-hard-orange">
      <div className="rounded-[1.4rem] bg-white overflow-hidden">
        <div className="h-6 bg-emerald-600 flex items-center justify-center relative">
          <span className="absolute left-1/2 -translate-x-1/2 top-1 w-8 h-1.5 rounded-full bg-stone-900/40" />
        </div>
        <div className="px-2.5 py-2">
          <div className="text-[9px] font-extrabold text-stone-900">Fresh Fruits</div>
          <div className="text-[7px] text-stone-500 mb-2">Your store, on Android</div>
          <div className="space-y-1.5">
            {[["#FDE68A", "Alphonso Mango", "₹399"], ["#BBF7D0", "Baby Spinach", "₹49"], ["#FBCFE8", "Kashmir Apple", "₹210"]].map(([c, n, p], i) => (
              <div key={i} className="flex items-center gap-1.5 rounded-lg border border-stone-200 p-1">
                <span className="w-6 h-6 rounded-md" style={{ background: c }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[7.5px] font-semibold text-stone-800 truncate">{n}</div>
                  <div className="text-[7.5px] text-emerald-700 font-mono-nums">{p}</div>
                </div>
                <span className="w-4 h-4 rounded bg-emerald-600 text-white text-[9px] leading-none flex items-center justify-center">+</span>
              </div>
            ))}
          </div>
          <div className="mt-2 h-6 rounded-lg bg-orange-500 text-white text-[8px] font-bold flex items-center justify-center">Checkout</div>
        </div>
      </div>
    </div>
  );
}

const TICKER = ["Razorpay", "Cashfree", "WhatsApp Checkout", "Cash on Delivery", "Free SSL", "SEO", "Coupons", "Wishlist", "Abandoned cart", "Discounts"];

export default function LandingClassic() {
  const cfg = useSiteConfig();
  const pricing = cfg.pricing;
  const themes = cfg.theme_showcase;
  const intro = cfg.intro_video;
  const videoT = cfg.video_testimonials;
  const textT = cfg.text_testimonials;
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = useRef(null);
  const scrollVideos = (dir) => videoRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  const plans = pricing?.plans || [];

  return (
    <div className="lp-paper text-stone-900 min-h-screen font-sans antialiased overflow-x-hidden pb-16 md:pb-0">
      <SiteHead path="/" />
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-stone-900/10 bg-[#FBF7F0]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand"><Brand variant="light" size="xl" /></Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-stone-600">
            <a href="#app" className="hover:text-stone-900">Ecom App</a>
            <Link to="/blog" className="hover:text-stone-900" data-testid="landing-blog-link">Blog</Link>
            <a href="#themes" className="hover:text-stone-900">See Demo</a>
            <a href="#pricing" className="hover:text-stone-900">Pricing</a>
          </nav>
          <div className="hidden md:flex items-center gap-2.5">
            <Link to="/login" className="text-sm font-medium text-stone-700 hover:text-stone-900 px-2" data-testid="landing-login-link">Sign in</Link>
            <Link to="/signup?plan=website" data-testid="landing-cta-signup">
              <Button className="bg-stone-900 hover:bg-stone-800 text-white font-semibold rounded-full px-5 border-2 border-stone-900 lp-hard-sm">Get Started</Button>
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border-2 border-stone-900 bg-white lp-hard-sm"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile menu panel */}
        {menuOpen && (
          <div className="md:hidden border-t border-stone-900/10 bg-[#FBF7F0]" data-testid="mobile-menu-panel">
            <nav className="max-w-6xl mx-auto px-5 py-4 flex flex-col gap-1 text-base font-medium text-stone-700">
              <a href="#app" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-stone-900/5">Ecom App</a>
              <Link to="/blog" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-stone-900/5">Blog</Link>
              <a href="#themes" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-stone-900/5">See Demo</a>
              <a href="#pricing" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-stone-900/5">Pricing</a>
              <div className="flex items-center gap-3 pt-4">
                <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center py-2.5 rounded-full border-2 border-stone-900 bg-white font-semibold" data-testid="mobile-menu-login">Sign in</Link>
                <Link to="/signup?plan=website" onClick={() => setMenuOpen(false)} className="flex-1" data-testid="mobile-menu-signup">
                  <Button className="w-full bg-stone-900 hover:bg-stone-800 text-white font-semibold rounded-full border-2 border-stone-900">Get Started</Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative lp-glow">
        <div className="absolute inset-0 lp-dots opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-stone-900 lp-hard-sm px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-orange-500" /> Specially Designed For Indian Startups
            </span>
            <h1 className="mt-6 font-display text-[2.7rem] leading-[1.05] sm:text-6xl font-extrabold tracking-tight">
              Your online store,
              <br />
              live for just <span className="lp-marker">{rupee(plans[0]?.price || 1999)}/year</span>.
            </h1>
            <p className="mt-5 text-lg text-stone-600 max-w-md">
              A full ecommerce website — cart, payments, SEO and more. No hosting bills, no middlemen,
              <span className="font-semibold text-stone-900"> no surprise mid-year renewals.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup?plan=website" data-testid="hero-cta-primary">
                <Button className="h-12 px-7 text-base bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full border-2 border-stone-900 lp-hard lp-hover-lift">
                  Build my store <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button variant="ghost" className="h-12 px-6 text-base font-semibold rounded-full border-2 border-stone-900 bg-white hover:bg-stone-50">See pricing</Button>
              </a>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-600">
              {["No coding", "Go live today", "Cancel anytime"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t}</span>
              ))}
            </div>
          </div>

          {/* Device stack */}
          <div className="relative mx-auto w-full max-w-md">
            <div className="lp-float"><BrowserMock /></div>
            <div className="absolute -bottom-8 -right-2 sm:right-2 lp-float-2"><PhoneMock /></div>
            <div className="absolute -top-4 -left-3 rotate-[-8deg] bg-white border-2 border-stone-900 lp-hard-sm rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">
              <BadgeCheck className="w-4 h-4 text-emerald-600" /> COD ready
            </div>
            <div className="absolute top-24 -right-4 rotate-[7deg] bg-orange-500 text-white border-2 border-stone-900 lp-hard-sm rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">
              <IndianRupee className="w-4 h-4" /> Razorpay + Cashfree
            </div>
          </div>
        </div>

        {/* TICKER */}
        <div className="border-y-2 border-stone-900 bg-stone-900 text-white overflow-hidden">
          <div className="lp-ticker py-3">
            {[...TICKER, ...TICKER].map((t, i) => (
              <span key={i} className="mx-6 text-sm font-semibold uppercase tracking-wider inline-flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-orange-400 fill-orange-400" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE'RE ABOUT — 16:9 intro video (right after hero) */}
      {intro && (
        <section id="about" className="max-w-6xl mx-auto px-5 pt-20 pb-4">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2">
              <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Watch the 2-min tour</span>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold leading-tight">{intro.heading}</h2>
              {intro.subheading && <p className="mt-4 text-stone-600">{intro.subheading}</p>}
              <div className="mt-6 flex flex-wrap gap-2">
                {["Website", "Payments", "Your own app"].map((t) => (
                  <span key={t} className="rounded-full bg-white border-2 border-stone-900 lp-hard-sm px-3 py-1 text-xs font-bold">{t}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3">
              <VideoPlayer url={intro.video_url} poster={intro.thumbnail_url} />
            </div>
          </div>
        </section>
      )}

      {/* FEATURES BENTO */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Everything included</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">One plan. Packed with features.</h2>
          <p className="mt-3 text-stone-600">The tools big brands pay lakhs for — bundled for Indian startups at a price that makes sense.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className={`rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm lp-hover-lift p-5 ${f.span || ""}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneMap[f.tone]}`}><Icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-display font-bold text-lg leading-snug">{f.title}</h3>
                <p className="mt-1.5 text-sm text-stone-600">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FLUTTER APP SPOTLIGHT */}
      <section id="app" className="max-w-6xl mx-auto px-5 pb-6">
        <div className="relative rounded-[2rem] bg-stone-900 text-white border-2 border-stone-900 overflow-hidden">
          <div className="absolute inset-0 lp-dots opacity-[0.15]" />
          <div className="relative grid lg:grid-cols-2 gap-8 p-8 sm:p-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-3 py-1 text-xs font-bold uppercase tracking-wide">
                <Smartphone className="w-4 h-4" /> Most loved feature
              </span>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold leading-tight">
                Get your store&apos;s own <span className="text-orange-400">Android app</span> — in one click.
              </h2>
              <p className="mt-4 text-stone-300 max-w-md">
                Add the Flutter app for <span className="font-bold text-white">{rupee((plans[1]?.price || 5998) - (plans[0]?.price || 1999))}/year</span>.
                Tap one button — <span className="text-white font-semibold">our servers build a signed app for you</span> and we help you
                get it approved on the Play Store.
              </p>
              <ul className="mt-6 space-y-2.5">
                {["Built automatically on our servers — zero coding", "Signed APK + Play Store bundle (AAB)", "Play Store approval support included", "Same catalog, orders & payments as your website"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-stone-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/signup?plan=website_app"><Button className="mt-8 h-12 px-7 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full border-2 border-white/10">Get website + app <MoveRight className="w-5 h-5 ml-1" /></Button></Link>
            </div>
            <div className="relative flex justify-center items-center py-6 min-h-[360px]">
              <div className="scale-[1.4] sm:scale-[1.55] lp-float"><PhoneMock /></div>
              <div className="absolute top-4 left-2 sm:left-6 rotate-[-6deg] bg-white text-stone-900 border-2 border-stone-900 lp-hard-sm rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">
                <Star className="w-4 h-4 text-orange-500 fill-orange-500" /> 4.9 on Play Store
              </div>
              <div className="absolute bottom-6 right-2 sm:right-10 rotate-[6deg] bg-white text-stone-900 border-2 border-stone-900 lp-hard-sm rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-orange-500" /> One-click build
              </div>
              <div className="absolute top-1/2 -right-1 sm:right-4 -translate-y-1/2 rotate-[4deg] bg-emerald-500 text-white border-2 border-stone-900 lp-hard-sm rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4" /> Approved
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THEME SHOWCASE (dynamic) */}
      <section id="themes" className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Live examples</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">{themes?.heading || "Themes that look made-to-order"}</h2>
          {themes?.subheading && <p className="mt-3 text-stone-600">{themes.subheading}</p>}
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(themes?.items || []).map((t, i) => {
            const themeId = normalizeStorefrontTheme(t.storefront_theme);
            return (
            <div
              key={t.id || i}
              data-testid={`theme-card-${i}`}
              className="group rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm lp-hover-lift overflow-hidden flex flex-col"
            >
              <div className="h-44 bg-stone-100 overflow-hidden border-b-2 border-stone-900">
                {t.image_url && <img src={mediaUrl(t.image_url)} alt={t.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-bold text-lg">{t.title}</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-stone-900 px-2 py-0.5">{themeId}</span>
                </div>
                <p className="mt-1.5 text-sm text-stone-600 flex-1">{t.description}</p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <a
                    href={t.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`theme-demo-${i}`}
                    className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-orange-600 hover:text-orange-700"
                  >
                    View demo <ArrowRight className="w-4 h-4" />
                  </a>
                  <CreateStoreWithTheme
                    themeId={themeId}
                    testId={`theme-create-${i}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-stone-900 text-white text-sm font-bold px-4 py-2 border-2 border-stone-900 hover:bg-orange-500 hover:border-orange-500 transition-colors"
                  >
                    Create Store
                  </CreateStoreWithTheme>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* VIDEO TESTIMONIALS (9:16, dynamic) */}
      {videoT && (videoT.items || []).length > 0 && (
        <section id="video-stories" className="max-w-6xl mx-auto px-5 py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Video stories</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">{videoT.heading || "Founders, in their own words"}</h2>
            {videoT.subheading && <p className="mt-3 text-stone-600">{videoT.subheading}</p>}
          </div>
          <div className="relative mt-10">
            <button
              type="button"
              onClick={() => scrollVideos(-1)}
              aria-label="Previous"
              data-testid="video-prev"
              className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border-2 border-stone-900 lp-hard-sm hover:bg-stone-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollVideos(1)}
              aria-label="Next"
              data-testid="video-next"
              className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border-2 border-stone-900 lp-hard-sm hover:bg-stone-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div ref={videoRef} className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 scroll-smooth sm:px-2">
              {(videoT.items || []).map((v, i) => (
                <div key={v.id || i} data-testid={`video-testimonial-${i}`} className="snap-center shrink-0 w-[78vw] sm:w-[270px]">
                  <div className="max-w-[270px] mx-auto">
                    <VideoPlayer url={v.video_url} poster={v.thumbnail_url} vertical />
                  </div>
                  {(v.name || v.role) && (
                    <div className="mt-3 px-1">
                      {v.name && <div className="font-display font-bold text-stone-900 leading-tight">{v.name}</div>}
                      {v.role && <div className="text-sm text-stone-500">{v.role}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Mobile arrows below */}
            <div className="flex sm:hidden items-center justify-center gap-3 mt-4">
              <button type="button" onClick={() => scrollVideos(-1)} aria-label="Previous" data-testid="video-prev-m" className="w-11 h-11 flex items-center justify-center rounded-full bg-white border-2 border-stone-900 lp-hard-sm"><ChevronLeft className="w-5 h-5" /></button>
              <button type="button" onClick={() => scrollVideos(1)} aria-label="Next" data-testid="video-next-m" className="w-11 h-11 flex items-center justify-center rounded-full bg-white border-2 border-stone-900 lp-hard-sm"><ChevronRight className="w-5 h-5" /></button>
            </div>
          </div>
        </section>
      )}

      {/* TEXT TESTIMONIALS (Google-review style, dynamic) */}
      {textT && (textT.items || []).length > 0 && (
        <section id="reviews" className="relative">
          <div className="absolute inset-0 lp-dots opacity-40 pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-5 py-20">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Reviews</span>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">{textT.heading || "Loved by Indian startups"}</h2>
              {textT.subheading && <p className="mt-3 text-stone-600">{textT.subheading}</p>}
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(textT.items || []).map((r, i) => (
                <div key={r.id || i} data-testid={`text-testimonial-${i}`} className="rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5 flex flex-col">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-stone-200 border-2 border-stone-900 flex-shrink-0">
                      {r.image_url ? <img src={mediaUrl(r.image_url)} alt={r.title} className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-stone-900 leading-tight truncate">{r.title}</div>
                      <div className="flex items-center gap-1 text-[11px] text-stone-400">
                        <span className="font-semibold text-emerald-600">Verified</span> · MeraStartup
                      </div>
                    </div>
                    <svg className="ml-auto w-6 h-6 flex-shrink-0" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46z"/><path fill="#FBBC05" d="M11.69 28.18A13.2 13.2 0 0 1 11 24c0-1.45.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.94 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>
                  </div>
                  <div className="mt-3"><Stars n={Number(r.rating) || 5} /></div>
                  <p className="mt-3 text-sm text-stone-700 leading-relaxed flex-1">
                    <Quote className="w-4 h-4 text-orange-400 inline -mt-1 mr-1" />
                    {r.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING (dynamic) */}
      <section id="pricing" className="relative">
        <div className="absolute inset-0 lp-dots opacity-50 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-orange-600">Pricing</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">{pricing?.heading || "One honest price. Everything included."}</h2>
            {pricing?.subheading && <p className="mt-3 text-stone-600">{pricing.subheading}</p>}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6 items-start">
            {plans.map((p, i) => {
              const highlight = p.highlight;
              return (
                <div
                  key={p.id || i}
                  data-testid={`pricing-plan-${p.id || i}`}
                  className={`relative rounded-3xl border-2 border-stone-900 p-7 ${highlight ? "bg-stone-900 text-white lp-hard-orange" : "bg-white lp-hard"}`}
                >
                  {p.badge ? (
                    <span className="absolute -top-3.5 left-7 rotate-[-3deg] bg-orange-500 text-white border-2 border-stone-900 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide">{p.badge}</span>
                  ) : null}
                  <h3 className="font-display text-xl font-extrabold">{p.name}</h3>
                  <p className={`mt-1 text-sm ${highlight ? "text-stone-300" : "text-stone-600"}`}>{p.tagline}</p>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="font-display text-5xl font-extrabold font-mono-nums">{rupee(p.price)}</span>
                    <span className={`mb-1.5 text-sm ${highlight ? "text-stone-400" : "text-stone-500"}`}>/{p.period || "year"}</span>
                  </div>
                  <Link to={`/signup?plan=${p.id || "website"}`} data-testid={`pricing-cta-${p.id || i}`}>
                    <Button className={`mt-6 w-full h-12 rounded-full font-bold border-2 ${highlight ? "bg-orange-500 hover:bg-orange-600 text-white border-white/10" : "bg-stone-900 hover:bg-stone-800 text-white border-stone-900"}`}>
                      {p.cta_text || "Get started"} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <ul className="mt-7 space-y-3">
                    {(p.features || []).map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${highlight ? "text-emerald-400" : "text-emerald-600"}`} />
                        <span className={highlight ? "text-stone-100" : "text-stone-700"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {p.footnote ? (
                    <p className={`mt-6 text-xs ${highlight ? "text-stone-400" : "text-stone-500"}`}>{p.footnote}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {(cfg.seo?.faq || []).filter((x) => x?.q && x?.a).length > 0 && (
        <section className="max-w-6xl mx-auto px-5 pb-16" id="faq" data-testid="landing-faq">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Questions shop owners ask</h2>
          <p className="mt-2 text-stone-600 max-w-2xl">Straight answers — also marked up for Google FAQ rich results.</p>
          <dl className="mt-8 grid md:grid-cols-2 gap-4">
            {cfg.seo.faq.filter((x) => x?.q && x?.a).map((item, i) => (
              <div key={i} className="rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5">
                <dt className="font-display font-bold text-stone-900">{item.q}</dt>
                <dd className="mt-2 text-sm text-stone-600 leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* CTA BAND */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="rounded-[2rem] bg-orange-500 border-2 border-stone-900 lp-hard p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 lp-dots opacity-20" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Specially designed for Indian startups.</h2>
            <p className="mt-3 text-white/90 max-w-xl mx-auto">Join the shopkeepers, makers and founders launching online with MeraStartup — today.</p>
            <Link to="/signup?plan=website"><Button className="mt-7 h-13 px-8 py-3 text-base bg-white text-stone-900 hover:bg-stone-100 font-bold rounded-full border-2 border-stone-900 lp-hover-lift">Start my store for {rupee(plans[0]?.price || 1999)}/year <ArrowRight className="w-5 h-5 ml-1" /></Button></Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t-2 border-stone-900 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-10">
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm font-medium text-stone-600 pb-6 border-b border-stone-900/10">
            {POLICIES.map((p) => (
              <Link key={p.key} to={p.path} className="hover:text-stone-900" data-testid={`landing-footer-${p.key}`}>{p.label}</Link>
            ))}
          </div>
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Brand variant="light" size="sm" />
            <p className="text-sm text-stone-500">© {new Date().getFullYear()} Mera Startup · Founder Neel Varma</p>
            <div className="flex items-center gap-5 text-sm font-medium text-stone-600">
              <Link to="/blog" className="hover:text-stone-900">Blog</Link>
              <Link to="/login" className="hover:text-stone-900">Sign in</Link>
              <Link to="/signup?plan=website" className="hover:text-stone-900">Get Started</Link>
              <Link to="/super-admin/login" className="hover:text-stone-900">Admin</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t-2 border-stone-900 bg-[#FBF7F0]/95 backdrop-blur px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="leading-tight">
          <div className="text-[11px] text-stone-500">Launch your store</div>
          <div className="font-display font-extrabold text-stone-900 text-sm">Just {rupee(plans[0]?.price || 1999)}/year</div>
        </div>
        <Link to="/signup?plan=website" className="flex-shrink-0" data-testid="sticky-cta">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-5 border-2 border-stone-900">Get Started</Button>
        </Link>
      </div>

      <ChatWidget />
    </div>
  );
}
